import { useEffect, useMemo, useState } from "react"
import type { Meeting, MeetingTopic } from "@/schema"
import { Badge, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@ui/shad"
import { badgeVariants } from "@ui/shad/badge"
import { cn } from "@ui/utils"
import { Loader2, X } from "lucide-react"

import { useUpdateMeeting } from "@/hooks/api/meetings"

interface MeetingTopicFormProps {
  meeting: Meeting
  availableTopics: MeetingTopic[]
  autoFocus?: boolean
}

const sortTopics = (topics: MeetingTopic[]) => [...topics].sort((a, b) => a.name.localeCompare(b.name))

export default function MeetingTopicForm({ meeting, availableTopics, autoFocus = true }: MeetingTopicFormProps) {
  const updateMeeting = useUpdateMeeting()
  const selectedTopics = useMemo(() => sortTopics(meeting.meeting_topics ?? []), [meeting.meeting_topics])
  const [optimisticTopics, setOptimisticTopics] = useState(selectedTopics)
  const [searchQuery, setSearchQuery] = useState("")

  useEffect(() => setOptimisticTopics(selectedTopics), [selectedTopics])

  const updateTopics = (topics: MeetingTopic[]) => {
    if (updateMeeting.isPending) return

    const previousTopics = optimisticTopics
    setOptimisticTopics(sortTopics(topics))
    updateMeeting.mutate(
      { meetingId: meeting.id, data: { meeting_topic_ids: topics.map((topic) => topic.id) } },
      {
        onError: () => setOptimisticTopics(previousTopics),
      }
    )
  }

  const activeTopics = availableTopics.filter((topic) => !topic.archived_at)
  const filteredTopics = sortTopics(
    activeTopics
      .filter((topic) => !optimisticTopics.some((selected) => selected.id === topic.id))
      .filter((topic) => `${topic.name} ${topic.description}`.toLowerCase().includes(searchQuery.trim().toLowerCase()))
  )

  return (
    <>
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="text-sm font-medium">Selected topics</span>
        <span className="text-muted-foreground flex items-center gap-2 text-xs tabular-nums" role="status">
          {updateMeeting.isPending && <Loader2 className="size-3 animate-spin" aria-label="Saving topics" />}
          {optimisticTopics.length}
        </span>
      </div>
      <div className="max-h-64 overflow-y-auto p-3">
        <div className="flex min-h-6 flex-wrap items-center gap-2">
          {optimisticTopics.length === 0 && (
            <span className="text-muted-foreground px-2 text-xs">No topics selected</span>
          )}
          {optimisticTopics.map((topic) => (
            <button
              key={topic.id}
              type="button"
              aria-label={`Remove ${topic.name}`}
              title={topic.description}
              disabled={updateMeeting.isPending}
              className={cn(
                badgeVariants({ variant: "secondary" }),
                "hover:bg-tinted hover:border-border max-w-full cursor-pointer gap-1 text-left disabled:pointer-events-none disabled:opacity-50"
              )}
              onClick={() => {
                updateTopics(optimisticTopics.filter((selected) => selected.id !== topic.id))
              }}
            >
              <span className="min-w-0 break-words">{topic.name}</span>
              <X className="-mr-1 size-3 shrink-0" />
            </button>
          ))}
        </div>
      </div>
      <Command className="rounded-none border-t" shouldFilter={false}>
        <CommandInput
          placeholder="Search topics..."
          value={searchQuery}
          onValueChange={setSearchQuery}
          autoFocus={autoFocus}
          className="rounded-none"
        />
        <CommandList className="max-h-72">
          <CommandEmpty className="p-3 text-xs">
            {searchQuery.trim()
              ? "No topics found"
              : activeTopics.length
                ? "All active topics are selected"
                : "No topics available"}
          </CommandEmpty>
          <CommandGroup>
            {filteredTopics.map((topic) => (
              <CommandItem
                key={topic.id}
                value={topic.name}
                disabled={updateMeeting.isPending}
                onSelect={() => {
                  updateTopics([...optimisticTopics, topic])
                  setSearchQuery("")
                }}
                showRing
                disableHighlighting
                className="cursor-pointer p-1"
                title={topic.description}
              >
                <Badge variant="secondary" className="min-w-0 break-words whitespace-normal">
                  {topic.name}
                </Badge>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </>
  )
}
